import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#FBF7F1] text-[#1A1A1A] py-12 px-5 sm:px-6">
      <div className="max-w-3xl mx-auto">
        <Link to="/" data-testid="privacy-back"
          className="text-xs text-[#9A9A9A] hover:text-[#1A1A1A] inline-flex items-center gap-1 mb-6">
          <ArrowLeft className="h-3 w-3" /> Torna alla home
        </Link>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#9A9A9A] mb-2">Documento informativo</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight">Informativa sulla Privacy</h1>
        <p className="text-xs text-[#9A9A9A] mt-2">Ultimo aggiornamento: febbraio 2026 · v2</p>

        <div className="prose prose-invert mt-8 space-y-6 text-sm leading-relaxed text-[#1A1A1A]">
          <Section title="1. Titolare del trattamento">
            ORIO è una piattaforma digitale italiana. Il titolare del trattamento dei dati personali è
            ORIO S.r.l., con sede in Italia (contatto: privacy@orio.app).
          </Section>

          <Section title="2. Dati raccolti">
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Dati di registrazione</strong>: nome, email, password (cifrata).</li>
              <li><strong>Dati del cane</strong>: nome, razza, foto, microchip, dati sanitari, documenti caricati.</li>
              <li><strong>Geolocalizzazione</strong>: posizione GPS attuale e cronologia approssimata per emergenze, missioni e sistema di prevenzione anti-fuga.</li>
              <li><strong>Notifiche push</strong>: identificativo del dispositivo (subscription endpoint) per inviarti avvisi.</li>
              <li><strong>Dati di pagamento</strong>: gestiti direttamente da Stripe (non conserviamo numeri di carta).</li>
              <li><strong>Log tecnici</strong>: indirizzo IP, tipo di browser, log di accesso.</li>
            </ul>
          </Section>

          <Section title="3. Finalità">
            I tuoi dati servono a: erogare il servizio (passaporto, mappa, missioni, match),
            inviarti notifiche operative, rispettare obblighi di legge, prevenire abusi e frodi,
            migliorare la piattaforma. Non vendiamo i tuoi dati e non li condividiamo con terze parti per marketing.
          </Section>

          <Section title="4. Geolocalizzazione">
            ORIO utilizza la posizione del tuo smartphone per: pubblicare emergenze SOS,
            notificarti emergenze e missioni nella tua zona, abilitare il sistema di prevenzione anti-fuga
            (zone sicure apprese automaticamente), suggerirti partner di passeggiata vicini.
            Puoi disattivare l&apos;accesso alla posizione dalle impostazioni del browser/telefono in qualsiasi momento.
          </Section>

          <Section title="5. Base giuridica">
            Trattiamo i dati sulla base del contratto (per fornirti il servizio), del tuo consenso esplicito
            (geolocalizzazione, notifiche, marketing), e di obblighi di legge.
          </Section>

          <Section title="6. Conservazione">
            Conserviamo i dati finché il tuo account è attivo. Alla chiusura, i dati personali vengono cancellati
            entro 30 giorni, salvo quelli che dobbiamo conservare per legge (es. fatturazione).
          </Section>

          <Section title="7. I tuoi diritti">
            Hai diritto ad accesso, rettifica, cancellazione, limitazione, portabilità e opposizione al
            trattamento dei tuoi dati. Scrivi a <a className="underline" href="mailto:privacy@orio.app">privacy@orio.app</a>.
          </Section>

          <Section title="8. Sicurezza">
            Password cifrate con bcrypt, comunicazioni in HTTPS, accessi tracciati, principio del minimo privilegio.
          </Section>

          <Section title="9. Cookie">
            Usiamo cookie tecnici essenziali per autenticazione e sicurezza. Non usiamo cookie di profilazione di terze parti.
          </Section>

          <Section title="10. Fotocamera, microfono e file media">
            L&apos;app richiede l&apos;accesso a <strong>fotocamera</strong>, <strong>microfono</strong> e <strong>galleria foto/video</strong> esclusivamente
            quando decidi tu: per caricare la foto del tuo cane, i documenti sanitari, le storie video/foto della community
            e per registrare messaggi vocali. I file scelti vengono caricati sui nostri server e mostrati agli utenti secondo
            le impostazioni di visibilità che decidi tu. L&apos;app <strong>non accede mai a fotocamera o microfono in background</strong>.
          </Section>

          <Section title="11. Cancellazione dell&apos;account">
            Puoi cancellare il tuo account in qualsiasi momento da <strong>Profilo → Impostazioni → Cancella account</strong>
            (endpoint tecnico: <code className="text-xs bg-black/5 px-1 py-0.5 rounded">POST /api/auth/delete-account</code>).
            La cancellazione è immediata: i tuoi dati personali (email, nome, telefono, foto) vengono anonimizzati subito;
            i contenuti pubblici (storie, missioni completate) restano solo se necessari alla community, in forma anonima.
            I dati fiscali obbligatori per legge (fatture Stripe) restano conservati per 10 anni come richiesto dal Fisco italiano,
            poi vengono eliminati automaticamente. Puoi anche richiedere la cancellazione scrivendo a
            <a className="underline ml-1" href="mailto:privacy@orio.app">privacy@orio.app</a>.
          </Section>

          <Section title="12. Servizi di terze parti">
            Per erogare il servizio ORIO si appoggia a questi fornitori (data processor esterni):
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong>Stripe Inc.</strong> — elaborazione pagamenti e fatturazione ricorrente (dati carta cifrati end-to-end).</li>
              <li><strong>MongoDB Atlas</strong> — hosting del database (server nell&apos;EU).</li>
              <li><strong>Emergent</strong> — hosting dell&apos;applicazione.</li>
              <li><strong>OpenStreetMap · Overpass</strong> — mappe e aree cani/veterinari (nessun dato personale inviato).</li>
              <li><strong>Open-Meteo</strong> — dati meteo per allerte (nessun dato personale inviato).</li>
              <li><strong>NASA FIRMS</strong> — dati incendi per allerte radar (nessun dato personale inviato).</li>
              <li><strong>Kevin MacLeod / incompetech.com</strong> — musiche royalty-free per Stories (streaming pubblico).</li>
              <li><strong>Gmail SMTP</strong> — invio email transazionali.</li>
              <li><strong>Google Play Console / App Store Connect</strong> — solo se hai scaricato l&apos;app dallo store: Google/Apple ricevono un identificativo tecnico anonimo per il funzionamento del sistema di installazione e notifiche. Non ricevono i tuoi dati.</li>
            </ul>
          </Section>

          <Section title="13. Contatti">
            Titolare: ORIO S.r.l., Italia · Data Protection Officer: <a className="underline" href="mailto:privacy@orio.app">privacy@orio.app</a>
            <br />Autorità di controllo: Garante per la protezione dei dati personali — <a className="underline" href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer">garanteprivacy.it</a>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-display text-xl mb-2 text-[#1A1A1A]">{title}</h2>
      <div>{children}</div>
    </div>
  );
}
